import { useEffect, useMemo, useState, type ReactNode } from 'react'
import BookWorkContent from './BookWorkContent'
import { fetchBookWork, type BookWorkResponse } from './bookWorkApi'
import { capturedBookScope, cardBookProposalIds, ordinaryBookCard } from './bookActionsPanel'
import type { DeckCard } from './types'

interface Props {
  groupId: string
  card: Pick<DeckCard, 'stage' | 'commitment' | 'rows' | 'objects' | 'claims'>
  fundIds?: readonly string[]
  fallback: ReactNode
  onRefresh?: () => void
  onApplied?: () => void
  onPendingChange?: (pending: boolean) => void
}

interface ReadyState {
  response: BookWorkResponse
}

export default function BookActionsPanel({
  groupId,
  card,
  fundIds,
  fallback,
  onRefresh,
  onApplied,
  onPendingChange,
}: Props) {
  const proposalIds = useMemo(() => cardBookProposalIds(card), [card])
  const proposalKey = proposalIds.join('|')
  const scope = useMemo(() => capturedBookScope(fundIds), [fundIds])
  const ordinary = useMemo(
    () => ordinaryBookCard(card, groupId, proposalIds),
    [card, groupId, proposalIds]
  )
  const [ready, setReady] = useState<ReadyState | null>(null)

  useEffect(() => {
    let cancelled = false
    setReady(null)
    if (!ordinary)
      return () => {
        cancelled = true
      }
    fetchBookWork(groupId, proposalIds, scope)
      .then((response) => {
        if (!cancelled) setReady({ response })
      })
      .catch(() => {
        // The legacy card remains the complete fallback for unsupported,
        // unreviewed capital, stale, or temporarily unavailable reads.
        if (!cancelled) setReady(null)
      })
    return () => {
      cancelled = true
    }
  }, [groupId, ordinary, proposalIds, proposalKey, scope])

  if (!ready) return <>{fallback}</>
  return (
    <section data-testid="book-actions-panel">
      <BookWorkContent
        groupId={groupId}
        work={ready.response}
        onRefresh={onRefresh}
        onApplied={onApplied}
        onPendingChange={onPendingChange}
      />
    </section>
  )
}
